import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';

import { LocationNodeData, QueryOption } from '../@types';
import {
  BatchReceive,
  BatchReceiveListResponse,
  CreateShipmentData,
  IncomingShipmentType,
  Shipment,
  ShipmentByPartData,
  ShipmentListResponse,
  ShipmentManifestSuppliers,
  ShipmentManifestSummery,
  ShipmentServiceAllocationList,
  ShipmentServiceAllocations,
  ShipmentStatus,
  ShipmentType,
  ReturnSuggestion,
} from '../@types/shipment.type';
import { searchReturnPageDestination, searchReturnPageSource } from '../api/live-search';
import {
  getReturnShipmentSuggestions,
  createAddStockManifestShipment,
  createNonSerializedManifestShipment,
  createShipment,
  deleteNonSerializedManifests,
  deleteSerializedManifests,
  getAllShipmentManifestsByShipmentId,
  getAllShipmentStatus,
  getShipmentBatchReceive,
  getShipmentById,
  getShipments,
  updateShipment,
  getAllShipmentTypes,
  getSerialValues,
  createFreshStockManifestShipment,
  deleteSerializedManifestById,
  getAllShipmentManifest,
  getShipmentBatchReceiveById,
  createShipmentBatchReceive,
  getAllManifestByShipment,
  createShipmentIndividualReceive,
  getShipmentServiceAllocationList,
  createShipmentServiceAllocation,
  deleteShipmentServiceAllocation,
  getIncomingShipments,
  getShipmentServiceAllocationsByShipmentId,
  getAddStockShipmentManifestPricing,
  getShipmentDetailsPartIncludes,
  getLatestShipmentByPartId,
  bulkCreateShipmentIndividualReceive,
  ShipmentManifestResponse,
  ServiceAllocationResponse,
} from '../api/shipment';
import { AddStockManifestRowItemValueType } from '../components/organisms/AddStockManifestRowItem';
import { ColumnSelectionItem } from '../components/templates/ColumnSelection';
import { ServiceLogAllKey } from '../configs/services';
import {
  ShipmentAddStockManifestKey,
  ShipmentBatchReceiveKey,
  ShipmentStatusKey,
  ShipmentsKey,
  ShipmentsReturnSuggestionsKey,
  ShipmentTypeKey,
  ShipmentManifestKey,
  ShipmentBatchReceiveOneKey,
  ShipmentIndividualReceiveKey,
  ShipmentServiceAllocationListKey,
  ShipmentIncomingShipmentsKey,
  ShipmentServiceAllocationsKey,
  ShipmentManifestSuppliersKey,
  ShipmentsReturnSourceSuggestionKey,
  ShipmentsReturnDestinationSuggestionKey,
  LatestShipmentByPartIdKey,
} from '../configs/shipment';
import { AddStockManifestDataType } from '../pages/Shipments/AddStock/AddStockManifestFragment';
import { getSelectedColumnNames } from '../utils/columnSelectionOperations';

// Both create and update invalidate the same set so the detail page and list
// views refresh together. Centralising here keeps cache behaviour predictable.
const invalidateShipmentCaches = async (
  queryClient: ReturnType<typeof useQueryClient>,
  shipmentId?: number
) => {
  if (shipmentId) {
    await queryClient.invalidateQueries({ queryKey: [ShipmentsKey, shipmentId] });
  }
  await queryClient.invalidateQueries({ queryKey: [ShipmentsKey] });
};

export const useCreateShipment = (): UseMutationResult<Shipment, Error, CreateShipmentData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateShipmentData) => createShipment(data),
    onSuccess: async (response) => {
      await invalidateShipmentCaches(queryClient, response.id);
    },
  });
};

export const useUpdateShipment = (): UseMutationResult<
  Shipment,
  Error,
  (Partial<Shipment> & { id: number }) | (Partial<Shipment> & { id?: number })
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Shipment> & { id?: number }) => {
      if (!data.id) throw new Error('Shipment ID is required for update');
      return await updateShipment(data.id, data);
    },
    retry: 2,
    onSuccess: async (response) => {
      await invalidateShipmentCaches(queryClient, response.id);
    },
  });
};

// Node-type columns are hidden by default, so getSelectedColumnNames would
// exclude them from the response. We always need them to gate the Source/
// Destination links (sibling/user nodes break /structure/:id) — and the
// shipmentListView computes them already — so they're force-kept in every
// response regardless of column visibility, at no extra request cost.
const ALWAYS_INCLUDED_SHIPMENT_FIELDS = ['sourceNodeType', 'destinationNodeType'];

export const useGetAllShipments = (
  queryOption: QueryOption,
  columnSelectionItems?: ColumnSelectionItem[]
): UseQueryResult<ShipmentListResponse> => {
  return useQuery({
    queryKey: [ShipmentsKey, queryOption.filterModel],
    queryFn: ({ signal }) => {
      const excludedColumns = getSelectedColumnNames(columnSelectionItems)
        .split(',')
        .filter((field) => field && !ALWAYS_INCLUDED_SHIPMENT_FIELDS.includes(field))
        .join(',');
      return getShipments(queryOption, excludedColumns, signal);
    },
    gcTime: 0,
  });
};

export const useGetOneShipment = (id: number): UseQueryResult<Shipment | undefined> => {
  return useQuery({
    queryKey: [ShipmentsKey, id],
    queryFn: async ({ signal }) => {
      if (!id) return undefined;
      return getShipmentById(id, signal);
    },
  });
};

// Imperative one-shot fetch of a shipment by id, on demand (e.g. "Copy From
// Existing Shipment" on the dispatch page). A mutation rather than a query so
// the caller triggers it from a button without an enabled/refetch dance.
export const useGetShipmentByIdMutation = (): UseMutationResult<Shipment, Error, number> => {
  return useMutation({
    mutationFn: (id: number) => getShipmentById(id),
  });
};

export const useGetAllShipmentStatus = (): UseQueryResult<ShipmentStatus[]> => {
  return useQuery({
    queryKey: [ShipmentStatusKey],
    queryFn: ({ signal }) => getAllShipmentStatus(signal),
  });
};

export const useGetAllShipmentBatchReceive = (
  queryOption: QueryOption,
  enabled: boolean = true
): UseQueryResult<BatchReceiveListResponse> => {
  return useQuery({
    queryKey: [
      ShipmentBatchReceiveKey,
      queryOption.filterModel,
      queryOption.page,
      queryOption.pageSize,
      queryOption.sortModel,
    ],
    queryFn: ({ signal }) => getShipmentBatchReceive(queryOption, signal),
    gcTime: 0,
    enabled,
  });
};

export const useGetReturnShipmentSuggestions = (
  sourceId: number,
  destinationId: number
): UseQueryResult<ReturnSuggestion[]> => {
  return useQuery({
    queryKey: [ShipmentsReturnSuggestionsKey, sourceId, destinationId],
    queryFn: ({ signal }) => getReturnShipmentSuggestions(sourceId, destinationId, signal),
    enabled: false,
  });
};

export const useGetAllShipmentTypes = (): UseQueryResult<ShipmentType[]> => {
  return useQuery({
    queryKey: [ShipmentTypeKey],
    queryFn: ({ signal }) => getAllShipmentTypes(signal),
  });
};

export const useGetAllShipmentManifestsByShipmentId = (
  shipmentId: number
): UseQueryResult<AddStockManifestDataType | undefined> => {
  return useQuery({
    queryKey: [ShipmentAddStockManifestKey, shipmentId],
    queryFn: async ({ signal }) => {
      if (shipmentId <= 0) return undefined;
      return getAllShipmentManifestsByShipmentId(shipmentId, signal);
    },
  });
};

export const useDeleteSerializedManifests = (): UseMutationResult<
  { id: number },
  Error,
  AddStockManifestRowItemValueType
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddStockManifestRowItemValueType) => {
      return await deleteSerializedManifests(data);
    },

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ShipmentAddStockManifestKey, response.id],
      });
      await queryClient.invalidateQueries({
        queryKey: [ShipmentAddStockManifestKey],
      });
    },
  });
};

export const useDeleteNonSerializedManifests = (
  shipmentId: number
): UseMutationResult<void, Error, { manifestId?: number }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { manifestId?: number }) => {
      if (!data.manifestId) throw new Error('Manifest ID is required for deletion');
      return await deleteNonSerializedManifests(shipmentId, data.manifestId);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ShipmentAddStockManifestKey, shipmentId],
      });
      await queryClient.invalidateQueries({
        queryKey: [ShipmentAddStockManifestKey],
      });
    },
  });
};

export const useCreateAddStockManifestShipment = (): UseMutationResult<
  { id: number },
  Error,
  AddStockManifestRowItemValueType[]
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddStockManifestRowItemValueType[]) => {
      const shipmentId = data[0]?.shipmentId;
      if (!shipmentId) {
        throw new Error('Shipment ID is required for creating add stock manifest');
      }
      return await createAddStockManifestShipment(Number(shipmentId), data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ShipmentAddStockManifestKey],
      });
    },
  });
};

export const useCreateNonSerializedManifestShipment = (): UseMutationResult<
  { id: number },
  Error,
  AddStockManifestRowItemValueType[]
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddStockManifestRowItemValueType[]) => {
      const shipmentId = data[0]?.shipmentId;
      if (!shipmentId) {
        throw new Error('Shipment ID is required for creating non-serialized manifest');
      }
      return await createNonSerializedManifestShipment(Number(shipmentId), data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ShipmentAddStockManifestKey],
      });
    },
  });
};

export const useGetSerialValues = (): UseMutationResult<
  { serials: Array<{ serial1: string; serial2?: string; serial3?: string }> },
  Error,
  AddStockManifestRowItemValueType
> => {
  return useMutation({
    mutationFn: async (data: AddStockManifestRowItemValueType) => {
      return await getSerialValues(data);
    },
  });
};

export const useCreateFreshStockManifestShipment = (): UseMutationResult<
  Shipment,
  Error,
  AddStockManifestRowItemValueType[]
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddStockManifestRowItemValueType[]) => {
      return await createFreshStockManifestShipment(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ShipmentAddStockManifestKey],
      });
    },
  });
};

export const useDeleteSerializedManifestById = (): UseMutationResult<void, Error, number> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: number) => {
      return await deleteSerializedManifestById(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ShipmentAddStockManifestKey],
      });
    },
  });
};

export const useGetShipmentBatchReceiveById = (
  batchId: string | number
): UseQueryResult<BatchReceive | undefined> => {
  return useQuery({
    queryKey: [ShipmentBatchReceiveOneKey, batchId],
    queryFn: async ({ signal }) => {
      if (batchId === 'create') return undefined;
      return getShipmentBatchReceiveById(Number(batchId), signal);
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetAllShipmentManifest = (
  partTypeId: number,
  nodeId: number
): UseQueryResult<ShipmentManifestResponse | undefined> => {
  return useQuery({
    queryKey: [ShipmentManifestKey, partTypeId, nodeId],
    queryFn: async ({ signal }) => {
      if (!partTypeId || !nodeId) return undefined;
      return getAllShipmentManifest(partTypeId, nodeId, signal);
    },
    refetchOnWindowFocus: false,
  });
};

export const useCreateShipmentBatchReceive = (): UseMutationResult<
  BatchReceive,
  Error,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: unknown) => {
      return await createShipmentBatchReceive(
        data as Parameters<typeof createShipmentBatchReceive>[0]
      );
    },

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ShipmentBatchReceiveOneKey, response.id],
      });
      await queryClient.invalidateQueries({
        queryKey: [ShipmentBatchReceiveKey],
      });
    },
  });
};

export const useGetAllManifestByShipment = (
  shipmentId: number
): UseQueryResult<ShipmentManifestResponse | undefined> => {
  return useQuery({
    queryKey: [ShipmentManifestKey, shipmentId],
    queryFn: async ({ signal }) => {
      if (shipmentId === 0) return undefined;
      return getAllManifestByShipment(shipmentId, signal);
    },
    enabled: false,
    refetchOnWindowFocus: false,
  });
};

export const useCreateShipmentIndividualReceive = (
  shipmentId: number
): UseMutationResult<{ id: number }, Error, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: unknown) => {
      const receiveData = data as Parameters<typeof createShipmentIndividualReceive>[1] & {
        shipmentId?: number;
      };
      return await createShipmentIndividualReceive(
        shipmentId || receiveData.shipmentId || 0,
        receiveData
      );
    },

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ShipmentIndividualReceiveKey, response.id],
      });
      await queryClient.invalidateQueries({
        queryKey: [ShipmentManifestKey, shipmentId],
      });
    },
  });
};

export const useBulkCreateShipmentIndividualReceive = (
  shipmentId: number
): UseMutationResult<{ id: number }, Error, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: unknown) => {
      return await bulkCreateShipmentIndividualReceive(
        shipmentId,
        data as Parameters<typeof bulkCreateShipmentIndividualReceive>[1]
      );
    },

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ShipmentIndividualReceiveKey, response.id],
      });
      await queryClient.invalidateQueries({
        queryKey: [ShipmentManifestKey, shipmentId],
      });
      await queryClient.invalidateQueries({
        queryKey: [ShipmentIncomingShipmentsKey],
      });
    },
  });
};

export const useGetShipmentServiceAllocationList = (
  serviceId: number
): UseQueryResult<ShipmentServiceAllocationList[] | undefined> => {
  return useQuery({
    queryKey: [ShipmentServiceAllocationListKey, serviceId],
    queryFn: async ({ signal }) => {
      if (!serviceId) return undefined;
      return getShipmentServiceAllocationList(serviceId, signal);
    },
  });
};

export const useCreateShipmentServiceAllocation = (): UseMutationResult<
  ServiceAllocationResponse,
  Error,
  Parameters<typeof createShipmentServiceAllocation>[0]
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Parameters<typeof createShipmentServiceAllocation>[0]) => {
      return await createShipmentServiceAllocation(data);
    },

    // BFF returns `{ isExist: true }` when the allocation already exists —
    // nothing was created, so there's nothing to invalidate.
    onSuccess: async (response) => {
      if ('isExist' in response) return;
      await queryClient.invalidateQueries({
        queryKey: [ShipmentServiceAllocationListKey, response.serviceId],
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, response.serviceId],
      });
    },
  });
};

export const useDeleteShipmentServiceAllocation = (
  serviceId: number
): UseMutationResult<void, Error, { id: number }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { id: number }) => {
      return await deleteShipmentServiceAllocation(data.id);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ShipmentServiceAllocationListKey, serviceId],
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, serviceId],
      });
    },
  });
};

export const useGetIncomingShipments = (
  shipmentStatus: string[]
): UseQueryResult<IncomingShipmentType[]> => {
  return useQuery({
    queryKey: [ShipmentIncomingShipmentsKey],
    queryFn: ({ signal }) => getIncomingShipments(shipmentStatus, signal),
  });
};

export const useGetShipmentServiceAllocationsByShipmentId = (
  shipmentId: number
): UseQueryResult<ShipmentServiceAllocations[] | undefined> => {
  return useQuery({
    queryKey: [ShipmentServiceAllocationsKey, shipmentId],
    queryFn: async ({ signal }) => {
      if (shipmentId === 0) return undefined;
      return getShipmentServiceAllocationsByShipmentId(shipmentId, signal);
    },
  });
};

export const useGetAddStockShipmentManifestPricing = (
  partTypeId: number
): UseQueryResult<ShipmentManifestSuppliers[]> => {
  return useQuery({
    queryKey: [ShipmentManifestSuppliersKey, partTypeId],
    queryFn: ({ signal }) => getAddStockShipmentManifestPricing(partTypeId, signal),
  });
};

export const useGetShipmentDetailsPartIncludes = (): UseMutationResult<
  ShipmentManifestSummery[],
  Error,
  { serviceId: number; partIds: number[] }
> => {
  return useMutation({
    mutationFn: async (data: { serviceId: number; partIds: number[] }) => {
      return await getShipmentDetailsPartIncludes(data.serviceId, data.partIds);
    },
  });
};

export const useGetReturnPageSourceSuggestions = (): UseQueryResult<LocationNodeData[]> => {
  return useQuery({
    queryKey: [ShipmentsReturnSourceSuggestionKey],
    queryFn: () => searchReturnPageSource(''),
    enabled: false,
  });
};

export const useGetReturnPageDestinationSuggestions = (): UseQueryResult<LocationNodeData[]> => {
  return useQuery({
    queryKey: [ShipmentsReturnDestinationSuggestionKey],
    queryFn: () => searchReturnPageDestination(''),
    enabled: false,
  });
};

export const useGetLatestShipmentByPartId = (
  partId: number
): UseQueryResult<ShipmentByPartData> => {
  return useQuery({
    queryKey: [LatestShipmentByPartIdKey],
    queryFn: ({ signal }) => getLatestShipmentByPartId(partId, signal),
  });
};
